/**
 * Pool-style CV drop / pick zone. Pair with ImportProgressPanel for the full
 * import visualization (4-step rail, elapsed timer, cancel).
 */
import React from 'react';
import type { UsePersonCvUploadResult } from '../../hooks/usePersonCvUpload';

const ACCEPT_ATTR = '.pdf,.doc,.docx,.txt,.md,.html,application/pdf';

interface Props {
  cvUpload: UsePersonCvUploadResult;
  disabled?: boolean;
  className?: string;
  idleLabel?: string;
  importingLabel?: string;
}

export const CvImportUploadZone: React.FC<Props> = ({
  cvUpload,
  disabled = false,
  className = '',
  idleLabel = 'Click to upload PDF or DOCX',
  importingLabel = 'Importing — please wait…',
}) => {
  const { fileInputRef, openFilePicker, onFileInputChange, busy, canUploadCv, importState } =
    cvUpload;

  const isImporting = busy && importState.mode === 'cv';
  const blocked = disabled || !canUploadCv || isImporting;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => !blocked && openFilePicker()}
        disabled={blocked}
        className={`w-full border border-dashed rounded-xl p-6 text-center transition-colors ${
          isImporting
            ? 'border-indigo-600/40 bg-indigo-950/20 opacity-80 cursor-wait'
            : blocked
              ? 'border-dark-border opacity-50 cursor-not-allowed'
              : 'border-dark-border hover:border-indigo-500/50 cursor-pointer bg-dark-bg'
        }`}
        data-testid="cv-import-upload-zone"
      >
        <span className="text-sm text-dark-text-secondary">
          {isImporting ? importingLabel : idleLabel}
        </span>
        {!isImporting && (
          <span className="block mt-1 text-[11px] text-dark-text-secondary/80">
            PDF, DOCX — auto-extracts skills (10–20s)
          </span>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={onFileInputChange}
        data-testid="profile-cv-file-input"
      />
    </div>
  );
};
